import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { JobsQueryParams, TimeRange } from './types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Remove duplicate values from an array, preserving first-seen order. */
export function unique<T>(items: T[]): T[] {
  return Array.from(new Set(items));
}

/**
 * Convert time range to ISO timestamp for filtering
 * Returns null for 'all' (no time filtering)
 */
export function getTimeRangeTimestamp(range: TimeRange): string | null {
  if (range === 'all') return null;

  const now = new Date();
  const minutes = {
    '15m': 15,
    '1h': 60,
    '24h': 24 * 60,
    '7d': 7 * 24 * 60,
    '30d': 30 * 24 * 60,
  }[range];

  if (!minutes) return null;

  const timestamp = new Date(now.getTime() - minutes * 60 * 1000);
  return timestamp.toISOString();
}

/**
 * Build query parameters for Jobs API with ANTLR syntax support
 *
 * Converts filter parameters to ANTLR query syntax (q parameter)
 * and adds other parameters directly.
 *
 * @example
 * buildJobsQuery({ status_name: 'running', limit: 10 })
 * // Returns URLSearchParams with: q=status_name.eq:'running'&limit=10
 *
 * @example
 * buildJobsQuery({ q: 'priority.gt:5', sort_by: 'create_ts:desc' })
 * // Returns URLSearchParams with: q=priority.gt:5&sort_by=create_ts:desc
 *
 * @param params - Query parameters
 * @param timeRangeFilter - Optional ISO timestamp to filter by create_ts >= timestamp
 */
export function buildJobsQuery(params?: JobsQueryParams, timeRangeFilter?: string | null): URLSearchParams {
  const queryParams = new URLSearchParams();

  if (!params) {
    return queryParams;
  }

  // Build ANTLR query string from filters
  const filters: string[] = [];

  if (params.status_name) {
    filters.push(`status_name.eq:'${params.status_name}'`);
  }

  // Add time range filter if provided
  if (timeRangeFilter) {
    filters.push(`create_ts.ge:'${timeRangeFilter}'`);
  }

  // If custom q is provided, add it to filters (combine with AND)
  if (params.q) {
    filters.push(params.q);
  }

  // Combine all filters with AND
  if (filters.length > 0) {
    queryParams.set('q', filters.join(' AND '));
  }

  // Add direct query parameters
  if (params.limit !== undefined) {
    queryParams.set('limit', String(params.limit));
  }
  if (params.offset !== undefined) {
    queryParams.set('offset', String(params.offset));
  }
  if (params.sort_by) {
    queryParams.set('sort_by', params.sort_by);
  }
  if (params.embed) {
    queryParams.set('embed', params.embed);
  }
  if (params.fields) {
    queryParams.set('fields', params.fields);
  }

  return queryParams;
}

/**
 * Format a duration between two timestamps
 * @param startTs - Start timestamp (ISO string)
 * @param completeTs - End timestamp (ISO string), uses current time if not provided
 * @returns Formatted duration string (e.g., "1h 30m", "5m 12s", "30s") or "-" if no start time or invalid
 */
export function formatDuration(startTs?: string | null, completeTs?: string | null): string {
  if (!startTs) return '-';
  const start = new Date(startTs);
  if (isNaN(start.getTime())) return '-';
  const end = completeTs ? new Date(completeTs) : new Date();
  if (completeTs && isNaN(end.getTime())) return '-';
  const ms = Math.max(0, end.getTime() - start.getTime()); // Guard against negative (clock skew)
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

/**
 * Resolve a paginated `next` link to a same-origin path.
 *
 * The API builds `next` as an absolute URL from its own public host, but the
 * dashboard is served from a different origin and reaches the API through a
 * same-origin `/v1/*` rewrite. Fetching that absolute URL leaves the origin and
 * triggers a CORS preflight the API does not answer, so every page after the
 * first is blocked. The `x-api-key` is not itself leaked in that case -- the
 * preflight fails before the real request carries it -- but a host that answers
 * its OWN preflight receives the key, which is what step 2 below is really for.
 *
 * The first two steps below are both needed, because each one alone still lets
 * the origin change; the third rejects what is left:
 *
 * 1. Parse against the current origin and keep only path and query. This
 *    resolves a host-relative value such as `//other.host/v1/jobs` and then
 *    discards the host it resolved to.
 * 2. Force exactly one leading slash. Step 1 is not enough on its own: a
 *    pathname can itself begin with `//`, and `fetch` reads a `//host/path`
 *    argument as a protocol-relative URL. A `next` of
 *    `https://host//evil.example/v1/jobs` survives step 1 as
 *    `//evil.example/v1/jobs` and would carry `x-api-key` to that host.
 *    Leading backslashes do the same, and an opaque path such as
 *    `mailto:v1/jobs` arrives with no leading slash at all, which `fetch`
 *    would resolve against the current route.
 * 3. Reject anything left that is not under `/v1/`, for the reason spelled out
 *    at the check itself.
 *
 * Two things throw, not one: a `next` that cannot be parsed, and a `next` that
 * parses cleanly but lands outside `/v1/`. Both mean the API is misbehaving,
 * and a silent fallback would hide that. What a throw costs depends on the
 * caller — the page walks in `useJobs` keep the pages they already hold, while
 * the infinite queries surface it as a query error.
 *
 * `/v1/` is hard-coded because the dashboard reaches the API only through its
 * own origin's `/v1/*` rewrite, which is also why `ApiConfig.baseUrl` is empty.
 * Pointing the dashboard at an absolute API host would leave first pages
 * working and every later page retargeted here; a different rewrite prefix or
 * API version has to be changed in this function too.
 */
export function toSameOriginPath(nextUrl: string): string {
  const parsed = new URL(nextUrl, window.location.origin);
  const path = `/${parsed.pathname.replace(/^[/\\]+/, '')}`;
  // Step 3: every paginated endpoint lives under `/v1/`, so anything else is a bad
  // link, and saying so here is the difference between one clear error and a
  // confusing one much later. A corrupt `X-Forwarded-Host` makes the API emit
  // `https:///v1/jobs/...`, which parses as host `v1` + path `/jobs/...`; without
  // this the walk would silently retarget the SPA fallback, which answers 200 with
  // `index.html` and only fails downstream as `Unexpected token '<'`.
  if (!path.startsWith('/v1/')) {
    throw new Error(`Refusing pagination link outside /v1/: ${path}`);
  }
  return `${path}${parsed.search}`;
}
